// The spirit's side of Phase 6: an evolving narrative keyed to what the player
// has actually found out, plus the questions they can put back to it.
//
// The central idea is that a spirit scene fires on a *discovery*, never on a
// death count. Rather than have every one of the ~20 action handlers in App.tsx
// remember to report the discovery it just caused, every trigger in Phase 6's
// list is derived here from whole GameState — so a discovery is recorded no
// matter which path produced it, exactly like the toast diffing in App.tsx.

import type {
  GameState,
  LoopState,
  PermanentState,
  SpiritQuestion,
  SpiritScene,
} from "../types"
import { getBattleById, getFactionById, getUnitById, spiritQuestions, spiritScenes } from "./content"

/** How many new questions the spirit will answer in a single loop. */
export const SPIRIT_AUDIENCES_PER_LOOP = 2

const AUDIENCE_FLAG_PREFIX = "spirit_audience_used_"

/**
 * Markers derivable from state alone. Anything that depends on an event the
 * state does not retain (which enemies a battle contained, say) is granted at
 * its own call site instead — see getEnemyFactionDiscoveries.
 */
function deriveDiscoveries(state: GameState): string[] {
  const { permanent, loop } = state
  const found: string[] = []

  if (permanent.totalDeaths > 0) found.push("died_once")
  if (permanent.deadlineRevealed) found.push("survived_day_30")

  if (Object.values(permanent.abilities).some((level) => level === "master")) {
    found.push("mastered_an_ability")
  }

  // "First survival of the orc attack" — the village standing at the end of
  // Day 2, whether that was won in the lane or arranged beforehand.
  if (
    loop.worldFlags.includes("dawnmere_orc_attack_repelled") ||
    (loop.currentDay > 2 &&
      loop.completedEvents.includes("dawnmere_orc_attack") &&
      !loop.worldFlags.includes("dawnmere_burned"))
  ) {
    found.push("survived_dawnmere_attack")
  }

  if (Object.values(loop.factionTrust).some((level) => level !== "unknown")) {
    found.push("met_a_faction")
  }
  if (loop.subjectedFactions.length > 0) found.push("subjected_a_faction")

  if (loop.currentLocationId === "ebon_citadel") found.push("reached_ebon_citadel")

  const anti = permanent.antiPlayerState
  if (anti.encounterCount > 0) found.push("met_anti_player")
  if (anti.defeatCount > 0) found.push("defeated_anti_player")
  if (anti.identityDiscovered) found.push("anti_player_identity_known")
  if (anti.motivationDiscovered) found.push("anti_player_motivation_known")
  if (anti.neutralized) found.push("anti_player_neutralized")

  return found
}

/**
 * The enemy factions a battle puts the player in front of. Encountering a
 * hostile faction for the first time is one of Phase 6's spirit triggers, and
 * the only record that it happened is the battle that was fought.
 */
export function getEnemyFactionDiscoveries(battleId: string): string[] {
  const battle = getBattleById(battleId)
  if (!battle) return []
  const factionIds = new Set<string>()
  for (const stack of battle.enemyStacks) {
    const factionId = getUnitById(stack.unitId)?.factionId
    if (!factionId) continue
    if (getFactionById(factionId)?.allied !== false) continue
    factionIds.add(factionId)
  }
  return [...factionIds].map((id) => `encountered_faction_${id}`)
}

/** Folds any newly-true discovery markers into permanent knowledge. */
export function recordSpiritDiscoveries(state: GameState): GameState {
  const discovered = deriveDiscoveries(state).filter(
    (id) => !state.permanent.knowledge.includes(id),
  )
  if (discovered.length === 0) return state
  return {
    ...state,
    permanent: {
      ...state.permanent,
      knowledge: [...state.permanent.knowledge, ...discovered],
    },
  }
}

function isSceneDue(scene: SpiritScene, permanent: PermanentState): boolean {
  return scene.requiresKnowledge.every((id) => permanent.knowledge.includes(id))
}

/**
 * The next scene the spirit owes the player, or null. Ordered so that when
 * several come due at once (a loop in which the player died, mastered a skill
 * and met the anti-player) they are told in the sequence they make sense in.
 */
export function getPendingSpiritScene(permanent: PermanentState): SpiritScene | null {
  return (
    [...spiritScenes]
      .sort((a, b) => a.order - b.order)
      .find(
        (scene) =>
          !permanent.spiritScenesSeen.includes(scene.id) && isSceneDue(scene, permanent),
      ) ?? null
  )
}

/** Scenes already delivered, newest last — the Spirit screen re-reads these freely. */
export function getSeenSpiritScenes(permanent: PermanentState): SpiritScene[] {
  return [...spiritScenes]
    .filter((scene) => permanent.spiritScenesSeen.includes(scene.id))
    .sort((a, b) => a.order - b.order)
}

/**
 * Marks a scene delivered and advances spiritProgress. Progress is simply the
 * number of scenes heard, which is what makes it a measure of discovery rather
 * than of deaths.
 */
export function markSpiritSceneSeen(
  permanent: PermanentState,
  sceneId: string,
): PermanentState {
  if (permanent.spiritScenesSeen.includes(sceneId)) return permanent
  return {
    ...permanent,
    spiritScenesSeen: [...permanent.spiritScenesSeen, sceneId],
    spiritProgress: permanent.spiritProgress + 1,
  }
}

/** A scene id, given the dialogue it plays — so the dialogue screen can report back. */
export function getSceneIdForDialogue(dialogueId: string): string | null {
  return spiritScenes.find((scene) => scene.dialogueId === dialogueId)?.id ?? null
}

export interface SpiritQuestionEntry {
  question: SpiritQuestion
  unlocked: boolean
  /** Already answered in some earlier loop; re-reading costs no audience. */
  asked: boolean
}

export function getSpiritQuestions(permanent: PermanentState): SpiritQuestionEntry[] {
  return spiritQuestions.map((question) => ({
    question,
    unlocked: question.requiresKnowledge.every((id) => permanent.knowledge.includes(id)),
    asked: permanent.spiritQuestionsAsked.includes(question.id),
  }))
}

export function getSpiritAudiencesUsed(loop: LoopState): number {
  return loop.worldFlags.filter((flag) => flag.startsWith(AUDIENCE_FLAG_PREFIX)).length
}

export function getSpiritAudiencesRemaining(loop: LoopState): number {
  return Math.max(0, SPIRIT_AUDIENCES_PER_LOOP - getSpiritAudiencesUsed(loop))
}

/**
 * Spends one of the loop's audiences. Audiences are loop state (the spirit's
 * attention is finite within a loop) while the answers themselves are
 * permanent, so a question paid for once is readable in every loop after.
 */
export function consumeSpiritAudience(loop: LoopState): LoopState {
  const used = getSpiritAudiencesUsed(loop)
  return {
    ...loop,
    worldFlags: [...loop.worldFlags, `${AUDIENCE_FLAG_PREFIX}${used + 1}`],
  }
}

export function markSpiritQuestionAsked(
  permanent: PermanentState,
  questionId: string,
): PermanentState {
  if (permanent.spiritQuestionsAsked.includes(questionId)) return permanent
  return {
    ...permanent,
    spiritQuestionsAsked: [...permanent.spiritQuestionsAsked, questionId],
  }
}
